import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { ForgotPasswordConfirmation } from '@/components/content/ForgotPassword/parts/ForgotPasswordConfirmation';
import { SendForgotPasswordCode } from '@/components/content/ForgotPassword/parts/SendForgotPasswordCode';
import { SendForgotPasswordEmail } from '@/components/content/ForgotPassword/parts/SendForgotPasswordEmail';
import {
	espotContainerSX,
	forgotPasswordContainerSX,
	formContainerSX,
} from '@/components/content/ForgotPassword/styles/container';
import {
	stepContainerSx,
	stepTitleDividerSx,
	stepTitleSx,
} from '@/components/content/ForgotPassword/styles/stepTitle';
import { useForgotPassword } from '@/data/Content/ForgotPassword';
import { ID } from '@/data/types/Basic';
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { FC, useState, useCallback } from 'react';
import { useForgotPasswordTitle } from '@/components/content/ForgotPassword/hooks/useForgotPasswordTitle';

const ESPOT_NAME = 'ForgotPass-AdditionalInfo';

export type Step = 0 | 1 | 2;

export const ForgotPassword: FC<{
	id: ID;
	isSessionDialog?: boolean;
	onStepChange?: (step: Step) => void;
	onGoToLogin?: () => void;
}> = ({ isSessionDialog, onStepChange, onGoToLogin }) => {
	const { sendForgotPasswordCode } = useForgotPassword();
	const [step, _setStep] = useState<Step>(0);
	const setStep = useCallback(
		(step: Step) => {
			_setStep(step);
			onStepChange?.(step);
		},
		[onStepChange]
	);
	const [email, setEmail] = useState('');

	const title = useForgotPasswordTitle({ step });

	const showEspot = step < 2 && !isSessionDialog;

	const handleEmailSuccess = (submittedEmail: string) => {
		setEmail(submittedEmail);
		setStep(1);
	};

	const handleResend = () => sendForgotPasswordCode(email);

	return (
		<Paper sx={forgotPasswordContainerSX}>
			<Grid container>
				{/* Main content block */}
				<Grid item xs={12} md={showEspot ? 4 : 12}>
					<Box sx={formContainerSX}>
						{/* Step title */}
						{isSessionDialog ? null : (
							<Stack sx={stepContainerSx}>
								<Typography variant="headingSegment" component="h1" sx={stepTitleSx}>
									{title}
								</Typography>
								<Divider sx={stepTitleDividerSx} />
							</Stack>
						)}

						{step === 0 && (
							<SendForgotPasswordEmail onSuccess={handleEmailSuccess} onGoToLogin={onGoToLogin} />
						)}
						{step === 1 && (
							<SendForgotPasswordCode
								email={email}
								onSuccess={() => setStep(2)}
								onResend={handleResend}
							/>
						)}
						{step === 2 && <ForgotPasswordConfirmation onGoToLogin={onGoToLogin} />}
					</Box>
				</Grid>

				{/* E-spot: right on desktop, bottom on mobile */}
				{showEspot && (
					<Grid item xs={12} md={8} sx={espotContainerSX}>
						<EMarketingSpot id={ESPOT_NAME} properties={{ emsName: ESPOT_NAME }} />
					</Grid>
				)}
			</Grid>
		</Paper>
	);
};
